import React, { FunctionComponent } from 'react';

// import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';

import SimpleArtist from '@common/types/artist/simple-artist';

import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Pill from '@components/basics/pill';
import StatusLabel from '@components/status-label';
import SquareImg from '@components/basics/artwork-img';
import Typography from '@components/basics/typography';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';
import { getNFTMenuOptions } from '@components/nft-row/menu-options';
import MerchColorChoice from '@components/merch-row/merch-color-choice';

import MenuDropdown from '@webapp/controllers/form/menu-dropdown';
import { NFTType } from '@webapp/pages/catalog/nft-tab';
import moment from 'moment';
import { formatNumber } from '@webapp/pages/earnings';

interface NFTDesktopInfoRowProps {
  className?: string;
  // TODO: DEVTEAM-603 - item needs to be integrated with the correct type.
  item: NFTType;
  artist: SimpleArtist;
  isListOpen: boolean;
  isArtist?: boolean;
  openList: () => void;
  handleCatalogMenu: (action: string, release: any) => void;
}

const NFTDesktopInfoRow: FunctionComponent<NFTDesktopInfoRowProps> = ({
  className,
  item,
  artist,
  isListOpen,
  openList,
  isArtist,
  handleCatalogMenu,
}) => {
  const theme = useTheme();
  const menuOptions = getNFTMenuOptions(item, artist);
  return (
    <Row className={`${className || ''} ${isListOpen ? 'menu-opened' : ''}`}>
      <Flex
        className="merch"
        justifyContent="space-between"
        alignItems="center"
        gap={20}
      >
        <Flex justifyContent="flex-start" alignItems="center" gap={20}>
          <SquareImg size={36} srcImg={item.image} altImg={item.title} />
          <Grid gap={5}>
            <Typography className="merch-name" customFont="semi-bold">
              {item.title}
            </Typography>
            {item.upc && (
              <Typography type="small" color={theme.colors.tertiary}>
                {item.upc}
              </Typography>
            )}
            {item.colors && <MerchColorChoice colors={item.colors} />}
          </Grid>
        </Flex>
        <Pill>{item.type}</Pill>
      </Flex>

      <div className="date-added">
        <Typography type="small">
          {moment(item.date_added).format('DD MMM YY')}
        </Typography>
      </div>

      <Grid className="price-profit" gap={2}>
        <Typography type="small">{formatNumber(item.editions)}</Typography>
      </Grid>

      <div className="status">
        <StatusLabel
          status={item.status}
          approved={item.status ? true : false}
        />
      </div>

      <Flex className="actions" alignItems="center" gap={5}>
        {!isArtist && (
          <StyledCircleArrowIcon
            isWhite
            smallArrow
            arrowUp={isListOpen}
            onClick={openList}
          />
        )}
        {menuOptions.length ? (
          <MenuButton>
            <MenuDropdown
              hasDottedIcon
              menuContent={menuOptions}
              onSelect={(action) => handleCatalogMenu(action, item)}
            />
          </MenuButton>
        ) : null}
      </Flex>
    </Row>
  );
};

export default NFTDesktopInfoRow;

const Row = styled.div`
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  width: 100%;
  display: grid;
  grid-template-columns: repeat(18, minmax(0, 1fr));
  grid-gap: 30px;
  align-items: center;
  padding: 30px;

  &.menu-opened {
    border-radius: 10px 10px 0 0;
  }

  .merch {
    grid-column: 1 / 7;
  }
  .date-added {
    grid-column: 7 / 9;
  }
  .price-profit {
    grid-column: 9 / 11;
  }
  .status {
    grid-column: 11 / 17;
  }
  .actions {
    grid-column: 17 / 19;
  }

  @media ${({ theme }) => theme.device.laptop} {
    .merch {
      grid-column: 1 / 9;
    }
    .date-added {
      grid-column: 9 / 11;
    }
    .price-profit {
      grid-column: 11 / 13;
    }
    .status {
      grid-column: 13 / 17;
    }
    .actions {
      grid-column: 17 / 19;
    }
  }

  .merch-name {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const MenuButton = styled.button`
  background-color: ${({ theme }) => theme.colors.differentBackground};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryHover};
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;

const StyledCircleArrowIcon = styled(CircleArrowIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

export const PublishingStatus = styled.div<{ progressWidth: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;
  padding: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 100px;
  width: fit-content;
  max-width: 100%;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: calc(100% - ${({ progressWidth }) => progressWidth + '%'});
    bottom: 0;
    left: 0;
    border-radius: 100px;
    background-color: ${({ theme }) => rgba(theme.colors.upload, 0.1)};
    transition: right
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  .publishing-text {
    color: ${({ theme }) => theme.colors.upload};
  }
`;
